/**
 * Grid Variants and Constants
 *
 * CVA variants and constants for the Grid component.
 * Exported separately to comply with React Fast Refresh requirements.
 *
 * @see Grid.tsx for component implementation
 */

import {cva} from 'class-variance-authority';

/**
 * Pre-defined responsive column configurations
 * These are common patterns that avoid arbitrary values
 *
 * IMPORTANT: Use md: (768px) not sm: (640px) for 2+ columns!
 * Many phones are 640px+ but still need single column layouts.
 */
export const GRID_PRESETS = {
  /** 1 col mobile, 2 cols tablet (768px+), 3 cols desktop - ideal for cards */
  cards: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
  /** 1 col mobile, 2 cols tablet (768px+), 4 cols desktop - ideal for feature grids */
  features: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
  /** 2 cols mobile, 3 cols tablet, 4 cols desktop - ideal for gallery (small thumbnails OK) */
  gallery: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4",
  /** 1 col mobile, 2 cols desktop - ideal for two-column layouts */
  twoCol: "grid-cols-1 lg:grid-cols-2",
  /** 1 col mobile, 3 cols desktop - ideal for three-column layouts */
  threeCol: "grid-cols-1 md:grid-cols-3",
  /** Full 12-column grid for complex layouts */
  grid12: "grid-cols-12",
} as const;

export type GridPreset = keyof typeof GRID_PRESETS;

export const gridVariants = cva("grid", {
  variants: {
    /**
     * Number of columns (static)
     */
    cols: {
      1: "grid-cols-1",
      2: "grid-cols-2",
      3: "grid-cols-3",
      4: "grid-cols-4",
      5: "grid-cols-5",
      6: "grid-cols-6",
      12: "grid-cols-12",
    },
    /**
     * Gap between items using Tailwind spacing scale
     */
    gap: {
      0: "gap-0",
      1: "gap-1",
      2: "gap-2",
      3: "gap-3",
      4: "gap-4",
      5: "gap-5",
      6: "gap-6",
      8: "gap-8",
      10: "gap-10",
      12: "gap-12",
      // Named scale
      xs: "gap-2",
      sm: "gap-3",
      md: "gap-4",
      lg: "gap-6",
      xl: "gap-8",
      "2xl": "gap-12",
    },
    /**
     * Alignment of items within cells (cross-axis)
     */
    align: {
      start: "items-start",
      center: "items-center",
      end: "items-end",
      stretch: "items-stretch",
    },
    /**
     * Justify items within cells
     */
    justify: {
      start: "justify-items-start",
      center: "justify-items-center",
      end: "justify-items-end",
      stretch: "justify-items-stretch",
    },
    /**
     * Whether the grid should fill its container
     */
    fullWidth: {
      true: "w-full",
      false: "",
    },
  },
  defaultVariants: {
    gap: "md",
    align: "stretch",
    justify: "stretch",
    fullWidth: true,
  },
});

export const gridItemVariants = cva("", {
  variants: {
    /**
     * Column span
     */
    colSpan: {
      1: "col-span-1",
      2: "col-span-2",
      3: "col-span-3",
      4: "col-span-4",
      5: "col-span-5",
      6: "col-span-6",
      full: "col-span-full",
    },
    /**
     * Row span
     */
    rowSpan: {
      1: "row-span-1",
      2: "row-span-2",
      3: "row-span-3",
      full: "row-span-full",
    },
  },
});
